import React from "react";
import { CoachSocialNetworkProfile } from "@/services/bedrock";
import { socialNetworkNames } from "@/interfaces";
import { Button, Dialog, Alert, Spinner } from "@/components/ui";
import { AlertCircle, Trash2 } from "lucide-react";

export interface DisconnectConfirmDialogProps {
  profile: CoachSocialNetworkProfile;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
}

export function DisconnectConfirmDialog({ profile, onClose, onConfirm, submitting }: DisconnectConfirmDialogProps) {
  const connectionCount = profile.activeChildConnectionsCount || 0;
  const platformName = socialNetworkNames[profile.socialNetworkId];

  return (
    <Dialog open onOpenChange={open => !open && onClose()} size="md" showCloseButton>
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-2 text-destructive font-bold">
          <AlertCircle className="size-5 text-destructive shrink-0 animate-bounce" />
          Disconnect Parent {platformName} Account?
        </Dialog.Title>
        <Dialog.Description>
          This is a highly destructive, irreversible action. Please review the details below before proceeding.
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="flex flex-col gap-4 py-3">
        <Alert
          variant="destructive"
          className="flex flex-col gap-2.5 border-destructive/20 bg-destructive/5 text-destructive"
        >
          <div className="flex items-center gap-2 font-bold text-sm">
            <Trash2 className="size-4 shrink-0" />
            <span>Warning: Mapped Connections Will Be Lost!</span>
          </div>
          <p className="text-xs m-0 leading-relaxed text-destructive/90">
            Disconnecting this parent {platformName} connection will{" "}
            <b>
              permanently remove and disconnect all {connectionCount} player connection
              {connectionCount === 1 ? "" : "s"}
            </b>{" "}
            currently assigned under it. Your players will lose posting capabilities to their child locations
            immediately.
          </p>
        </Alert>

        <div className="text-xs text-muted-foreground flex flex-col gap-1 border border-gray-100 p-3 rounded-lg bg-gray-50/50">
          <div className="flex justify-between py-1 border-b border-gray-200/50">
            <span className="font-semibold">Parent Login:</span>
            <span className="font-bold text-foreground">
              {profile.socialIdentity?.displayName || profile.socialIdentity?.username || "Parent Profile"}
            </span>
          </div>
          <div className="flex justify-between py-1">
            <span className="font-semibold">Active Mapped Players:</span>
            <span className="font-bold text-destructive">{connectionCount} Players</span>
          </div>
        </div>
      </Dialog.Content>

      <Dialog.Footer className="gap-2 shrink-0">
        <Button variant="outline" onClick={onClose} disabled={submitting}>
          Keep Connection
        </Button>
        <Button
          variant="destructive"
          onClick={onConfirm}
          disabled={submitting}
          className="font-bold flex items-center gap-1.5"
        >
          {submitting ? (
            <>
              <Spinner className="size-4 mr-2" /> Disconnecting...
            </>
          ) : (
            `Disconnect & Unlink ${connectionCount} Player${connectionCount === 1 ? "" : "s"}`
          )}
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
